import { type ReactNode } from 'react'
import PropTypes, { type ReactNodeLike } from 'prop-types'
import cx from 'classnames'
import { Card, ResponsiveContainer, DialogsRenderer } from '@theneatcompany/nui'
import { useRouter } from 'next/router'

import Copyright from '@/components/Copyright'
import { Button, Error, Link, Text } from '@/components/common'
import { getMessage } from '@/utils/messages'
import { QUERY_PARAMS, URLS } from '@/constants'
import useRecaptchaBadgePosition from '@/hooks/useRecaptchaBadgePosition'
import useClientConfig from '@/hooks/useClientConfig'
import useInvitation from '@/hooks/useInvitation'
import rollbar from '@/utils/rollbar'

import styles from './Layout.module.scss'

interface LayoutProps {
  children: ReactNode
  error?: string
  isLogin?: boolean
  renderLogo: (shouldRender: boolean) => ReactNode
  isInvitationsLogin?: boolean
  isOauthLogin?: boolean
}

interface LayoutHeaderProps {
  children: ReactNode
  centered?: boolean
}

interface LayoutCardProps {
  children: ReactNode & NonNullable<ReactNodeLike>
  size?: string
  className?: string
}

interface LayoutContentProps {
  children: ReactNode
  className?: string
  isLogin?: boolean
  isWide?: boolean
  isCreateAccount?: boolean
}

interface LayoutActionsProps {
  children: ReactNode
}

interface LayoutTextProps {
  children: ReactNode
  className?: string
  centered?: boolean
}

export default function Layout({
  children,
  error,
  isLogin = false,
  renderLogo,
  isInvitationsLogin = false,
  isOauthLogin = false,
}: LayoutProps) {
  const router = useRouter()
  const clientConfig = useClientConfig()
  const recaptchaBagePosition = useRecaptchaBadgePosition()
  const isDebugMode = router.query[QUERY_PARAMS.debug] === 'true'
  const invitation = useInvitation()

  // when recaptcha badge is rendered inline, copyright will be rendered by SignInLayout so that
  // the recaptcha badge is below all other elements
  const shouldRenderCopyright =
    !clientConfig.recaptchaEnabled || ((isLogin || isInvitationsLogin) && !error && recaptchaBagePosition !== 'inline')
  const shouldRenderFooter = clientConfig.ready && (shouldRenderCopyright || isDebugMode)

  const maybeRenderSubheader = () => {
    if (error || (!isLogin && !isInvitationsLogin)) return null

    return (
      <div className={styles.subheader}>
        {isLogin && <Text size="small" messageId="login.header" />}
        {isInvitationsLogin && (
          <>
            {invitation.email && (
              <Text size="small" element="p">
                {getMessage('invitations.login.greeting', { name: invitation.name || invitation.email })}
                <Button
                  variant="link"
                  onClick={() => {
                    rollbar.info('Invitations Login Not You? clicked', { email: invitation.email })
                    invitation.clearUserInfo()
                  }}
                  size="small"
                  messageId="invitations.login.notYou"
                />
              </Text>
            )}
            <Text
              size="small"
              element="p"
              className="line-break"
              messageProps={{ id: 'invitations.login.pendingShare', values: { type: invitation.type } }}
            />
          </>
        )}
        {isOauthLogin && (
          <Text size="small" element="p">
            {getMessage('login.oauthSignUp')}&nbsp;
            <Link to={URLS.signUp} external openTab size="small" messageId="login.tryFree">
              <></>
            </Link>
          </Text>
        )}
      </div>
    )
  }

  return (
    <>
      <div className={styles.container}>
        <div className={styles.header}>
          {renderLogo(!!error)}
          {maybeRenderSubheader()}
        </div>
        <div className={cx(styles.main, error && styles.error)}>{error ? <Error title={error} /> : children}</div>
        {shouldRenderFooter && (
          <div className={styles.footer}>
            {shouldRenderCopyright && <Copyright />}
            {isDebugMode && (
              <Text className={styles.buildDetails} element="p" size="tiny">
                <b>Tag:</b> {process.env.NEXT_PUBLIC_BUILD_TAG}&nbsp;|&nbsp;
                <b>Env:</b> {process.env.NEXT_PUBLIC_ENV}
              </Text>
            )}
          </div>
        )}
      </div>
      <DialogsRenderer />
    </>
  )
}

const Header = ({ children, centered = true, ...rest }: LayoutHeaderProps) => (
  <Text
    type="display"
    element="h2"
    className={cx(styles.layoutHeader, centered && 'text-align_center')}
    size="small"
    {...rest}
  >
    {children}
  </Text>
)

const LayoutCard = ({ children, size = 'sm', className, ...rest }: LayoutCardProps) => (
  <ResponsiveContainer size={size}>
    <Card withShadow className={cx(styles.card, className)} {...rest}>
      {children}
    </Card>
  </ResponsiveContainer>
)

const LayoutContent = ({
  children,
  className,
  isLogin = false,
  isWide = false,
  isCreateAccount,
  ...rest
}: LayoutContentProps) => (
  <div
    className={cx(
      styles.content,
      isLogin && styles.isLogin,
      isWide && styles.isWide,
      isCreateAccount && styles.isCreateAccount,
      className,
    )}
    {...rest}
  >
    {children}
  </div>
)

const Actions = ({ children, ...rest }: LayoutActionsProps) => (
  <div className={styles.actions} {...rest}>
    {children}
  </div>
)

const LayoutText = ({ children, className, centered = false, ...rest }: LayoutTextProps) => (
  <Text element="p" color="foreground-secondary" className={cx(className, centered && 'text-align_center')} {...rest}>
    {children}
  </Text>
)
Layout.Card = LayoutCard
Layout.Actions = Actions
Layout.Header = Header
Layout.Text = LayoutText
Layout.Content = LayoutContent

Layout.propTypes = {
  children: PropTypes.node.isRequired,
  error: PropTypes.string,
  isLogin: PropTypes.bool,
  renderLogo: PropTypes.func.isRequired,
  isInvitationsLogin: PropTypes.bool,
  isOauthLogin: PropTypes.bool,
}

LayoutCard.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
  size: PropTypes.string,
  className: PropTypes.string,
}

Actions.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
}

Header.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
  centered: PropTypes.bool,
}

LayoutText.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
  centered: PropTypes.bool,
  className: PropTypes.string,
}

LayoutContent.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
  size: PropTypes.string,
  className: PropTypes.string,
  isLogin: PropTypes.bool,
  isWide: PropTypes.bool,
  isCreateAccount: PropTypes.bool,
}
